<template>
  <div class="chart-wrap">
    <div v-if="loading" class="chart-skeleton" aria-hidden="true">
      <div class="chart-skeleton__legend">
        <span v-for="index in 3" :key="`legend-${index}`" class="chart-skeleton__pill" />
      </div>
      <div class="chart-skeleton__bars">
        <span v-for="index in 12" :key="`bar-${index}`" class="chart-skeleton__bar" />
      </div>
    </div>
    <canvas v-else ref="revenueChartCanvas" class="chart-canvas" />
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import { Chart, registerables } from 'chart.js'
import gql from 'graphql-tag'

// Register all Chart.js modules
Chart.register(...registerables)

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'revenue-bar-chart',
  props: {
    selectedYear: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      chart: null,
      chartData: {
        labels: [],
        datasets: {
          totalFees: [],
          ticketFees: [],
          otherFees: [],
        },
      },
    }
  },
  computed: { adminChartLocale() { return this.$adminLocale() } },
  watch: {
    adminChartLocale() {
      if (this.chart && !this.loading) this.renderChart()
    },
    async selectedYear() {
      await this.loadData()
      this.renderChart()
    },
  },
  async mounted() {
    await this.loadData()
    this.renderChart()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      this.chartData.labels = []
      this.chartData.datasets = { totalFees: [], ticketFees: [], otherFees: [] }

      for (let i = 0; i < 12; i++) {
        const date = new Date(this.selectedYear, new Date().getMonth() - i, 1)

        // Fetch data for each month sequentially
        const { data } = await this.$apollo.query({
          query: gql`
            query GetMonthlyReport($year: Int!, $month: Int!) {
              getCoreEventMonthlyReport(year: $year, month: $month)
            }
          `,
          variables: {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
          },
        })

        const report = data.getCoreEventMonthlyReport
        const ticketFees = Number(
          report.totalCoreEventTicketFeeEarnings != null
            ? report.totalCoreEventTicketFeeEarnings
            : report.totalCoreEventFixedFeeEarnings || 0,
        )
        this.chartData.labels.unshift(`${date.getMonth() + 1}/${date.getFullYear()}`)
        this.chartData.datasets.totalFees.unshift(report.totalCoreEventEarnings)
        this.chartData.datasets.ticketFees.unshift(ticketFees)
        this.chartData.datasets.otherFees.unshift(
          report.totalCoreEventEarnings - ticketFees,
        )
      }

      this.loading = false
    },
    renderChart() {
      if (this.chart) {
        this.chart.destroy()
      }

      const ctx = this.$refs.revenueChartCanvas.getContext('2d')
      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.chartData.labels,
          datasets: [
            {
              label: this.$adminT('adminAll.ukupno_naknada_75e82c', 'Ukupno Naknada'),
              data: this.chartData.datasets.totalFees,
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1,
              barThickness: 25, // Set thickness for this dataset
            },
            {
              label: this.$adminT('adminAll.ukupno_naknade_za_izdavanje_ulaznica_df24c9', 'Ukupno Naknade Za Izdavanje Ulaznica'),
              data: this.chartData.datasets.ticketFees,
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1,
              barThickness: 10, // Thinner bar thickness
            },
            {
              label: this.$adminT('adminAll.ukupno_ostale_naknade_e6a2df', 'Ukupno Ostale Naknade'),
              data: this.chartData.datasets.otherFees,
              backgroundColor: 'rgba(255, 206, 86, 0.2)',
              borderColor: 'rgba(255, 206, 86, 1)',
              borderWidth: 1,
              barThickness: 10, // Thinner bar thickness
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: this.$adminT('adminAll.monthly_revenue_report_last_12_months_db3068', 'Monthly Revenue Report - Last 12 Months'),
            },
          },
        },
      })
    },
  },
}
</script>

<style scoped>
.chart-wrap {
  width: 100%;
  height: 320px;
}
.chart-skeleton {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
  width: 100%;
  height: 100%;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}
.chart-skeleton__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.chart-skeleton__pill,
.chart-skeleton__bar {
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: chart-skeleton-shimmer 1.4s ease-in-out infinite;
}
.chart-skeleton__pill {
  width: 120px;
  height: 16px;
}
.chart-skeleton__bars {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  align-items: end;
  gap: 10px;
  flex: 1;
}
.chart-skeleton__bar {
  width: 100%;
}
.chart-skeleton__bar:nth-child(1) { height: 34%; }
.chart-skeleton__bar:nth-child(2) { height: 58%; }
.chart-skeleton__bar:nth-child(3) { height: 42%; }
.chart-skeleton__bar:nth-child(4) { height: 70%; }
.chart-skeleton__bar:nth-child(5) { height: 48%; }
.chart-skeleton__bar:nth-child(6) { height: 82%; }
.chart-skeleton__bar:nth-child(7) { height: 56%; }
.chart-skeleton__bar:nth-child(8) { height: 76%; }
.chart-skeleton__bar:nth-child(9) { height: 39%; }
.chart-skeleton__bar:nth-child(10) { height: 64%; }
.chart-skeleton__bar:nth-child(11) { height: 52%; }
.chart-skeleton__bar:nth-child(12) { height: 88%; }
.chart-canvas {
  width: 100% !important;
  height: 100% !important;
}
@keyframes chart-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
</style>
